import type {QueryClient} from "@tanstack/react-query";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {
    EVENTS_MODULE_DATA_VIEWER_QUERY_KEY,
    EVENT_CONTAINERS_MODULE_DATA_VIEWER_QUERY_KEY,
} from "~/utilities/archiveEventContainerWithEvents";

/**
 * Invalidates every data-viewer query for a module (table, list, kanban, calendar, row count).
 * Uses `refetchType: "all"` so inactive view caches refresh without a full page reload.
 */
export function invalidateOttopilotDataViewerQueries(
    queryClient: QueryClient,
    queryKeyPrefix: ReadonlyArray<unknown> | undefined,
): Promise<void> {
    const prefix = (queryKeyPrefix ?? []).filter(
        (segment): segment is string => typeof segment === "string",
    );
    return queryClient.invalidateQueries({
        queryKey: ["ottopilotDataViewer", ...prefix],
        refetchType: "all",
    });
}

/** After archiving a journey (and its events), refresh journeys list and all event templates in the workspace. */
export async function invalidateDataViewerQueriesAfterJourneyArchive(
    queryClient: QueryClient,
    journeysQueryKeyPrefix: ReadonlyArray<unknown>,
    workspaceId: Uuid,
): Promise<void> {
    await invalidateOttopilotDataViewerQueries(queryClient, journeysQueryKeyPrefix);
    await invalidateOttopilotDataViewerQueries(queryClient, [
        EVENTS_MODULE_DATA_VIEWER_QUERY_KEY,
        workspaceId,
    ]);
}

/** After archiving an event, refresh that template's list and journey modules in the workspace. */
export async function invalidateDataViewerQueriesAfterEventArchive(
    queryClient: QueryClient,
    eventsQueryKeyPrefix: ReadonlyArray<unknown>,
    workspaceId: Uuid,
): Promise<void> {
    await invalidateOttopilotDataViewerQueries(queryClient, eventsQueryKeyPrefix);
    await invalidateOttopilotDataViewerQueries(queryClient, [
        EVENT_CONTAINERS_MODULE_DATA_VIEWER_QUERY_KEY,
        workspaceId,
    ]);
}
